import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ResponsiveDialog, ResponsiveDialogFooter } from "@/components/responsive-dialog";
import { useSubmitWeek } from "@/api/me/mutations";

export interface SubmitWeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  week: string;
  onSubmitted: () => void;
}

export function SubmitWeekDialog({
  open,
  onOpenChange,
  userId,
  week,
  onSubmitted,
}: SubmitWeekDialogProps) {
  const { t } = useTranslation();
  const submit = useSubmitWeek(userId);

  // HoursEntry only carries attachmentId (string | null) — the week response does not
  // embed attachment records, so upload state cannot be verified here. Blocking on
  // in-progress uploads is handled by the upload-queue dispatcher.
  const blockingAttachments = false;

  async function onConfirm() {
    try {
      await submit.mutateAsync(week);
      onSubmitted();
    } catch {
      // Mutation surfaces via submit.error; global toast handles user notification
    }
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("work.submit.title", { week })}
    >
      <p className="text-[13px] text-ink-700">{t("work.submit.body")}</p>
      {blockingAttachments ? (
        <p className="mt-2 text-[13px] text-vroom-red">{t("work.submit.attachmentsBlocking")}</p>
      ) : null}
      <ResponsiveDialogFooter>
        <Button variant="ghost" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
          {t("common.buttons.cancel.label")}
        </Button>
        <Button
          variant="primary"
          className="w-full sm:w-auto"
          onClick={() => void onConfirm()}
          disabled={submit.isPending}
        >
          {t("work.submit.confirm")}
        </Button>
      </ResponsiveDialogFooter>
    </ResponsiveDialog>
  );
}
